// src/pages/RewardsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useProgram } from '../context/ProgramContext';
import apiClient from '../api';
import '../styles/RewardsPage.css';

const formatCost = (cost, program) => {
    if (program?.asset_type === 'points_on_chain') {
        const unit = program.asa_unit_name || 'tokens';
        return `${Number(cost).toLocaleString(undefined, { maximumFractionDigits: program.asa_decimals || 0 })} ${unit}`;
    }
    return `${Math.round(cost)} pts`;
};

const initialForm = { title: '', description: '', cost: '100', active: false, image: null };

const RewardsPage = ({ isAuthenticated }) => {
    const { selectedProgram } = useProgram();
    const [rewards, setRewards] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null); // null = create mode, number = edit mode
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    const fetchRewards = useCallback(async () => {
        if (!selectedProgram) { setRewards([]); return; }
        setIsLoading(true); setError('');
        try {
            const res = await apiClient.get(`/programs/${selectedProgram.id}/rewards`);
            setRewards(res.data || []);
        } catch (err) {
            setError(err.response?.data?.detail || err.message || 'Failed to load rewards.');
            setRewards([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedProgram]);

    useEffect(() => {
        if (isAuthenticated && selectedProgram) fetchRewards();
        else { setRewards([]); setIsLoading(false); setError(''); }
    }, [isAuthenticated, selectedProgram, fetchRewards]);

    const openCreateForm = () => {
        setEditingId(null);
        setForm(initialForm);
        setFormError('');
        setShowForm(true);
    };

    const openEditForm = (reward) => {
        setEditingId(reward.id);
        setForm({
            title: reward.title || '',
            description: reward.description || '',
            cost: String(reward.cost ?? '100'),
            active: !!reward.active,
            image: null, // file input always starts empty; existing image stays unless replaced
        });
        setFormError('');
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false); setEditingId(null); setForm(initialForm); setFormError('');
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        if (type === 'file') {
            setForm(f => ({ ...f, image: files && files[0] ? files[0] : null }));
        } else if (type === 'checkbox') {
            setForm(f => ({ ...f, [name]: checked }));
        } else {
            setForm(f => ({ ...f, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        const costNum = parseFloat(form.cost);
        if (!form.title.trim()) { setFormError('Title is required.'); return; }
        if (!(costNum > 0)) { setFormError('Cost must be greater than 0.'); return; }
        if (form.image && form.image.size > 2 * 1024 * 1024) {
            setFormError('Image must be 2 MB or smaller.'); return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('title', form.title.trim());
            fd.append('description', form.description || '');
            fd.append('cost', String(costNum));
            fd.append('active', form.active ? 'true' : 'false');
            if (form.image) fd.append('image_file', form.image);

            if (editingId == null) {
                await apiClient.post(`/programs/${selectedProgram.id}/rewards`, fd);
            } else {
                await apiClient.patch(`/rewards/${editingId}`, fd);
            }
            closeForm();
            await fetchRewards();
        } catch (err) {
            setFormError(err.response?.data?.detail || err.message || 'Save failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (reward) => {
        try {
            const fd = new FormData();
            fd.append('active', (!reward.active) ? 'true' : 'false');
            await apiClient.patch(`/rewards/${reward.id}`, fd);
            await fetchRewards();
        } catch (err) {
            alert(err.response?.data?.detail || err.message || 'Failed to update.');
        }
    };

    const handleDelete = async (reward) => {
        if (!window.confirm(`Delete reward "${reward.title}"? This cannot be undone.`)) return;
        try {
            await apiClient.delete(`/rewards/${reward.id}`);
            await fetchRewards();
        } catch (err) {
            alert(err.response?.data?.detail || err.message || 'Failed to delete.');
        }
    };

    if (!isAuthenticated) return <Navigate to="/dashboard" replace />;

    if (!selectedProgram) {
        return (
            <div className="rewards-container info-message">
                <h2>No Program Selected</h2>
                <p>Please <Link to="/select-program">select a program</Link> first to manage its rewards.</p>
            </div>
        );
    }

    const buildImgSrc = (url) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        return `${apiClient.defaults.baseURL}${url}`;
    };

    return (
        <div className="rewards-container">
            <div className="rewards-header">
                <div>
                    <h2>Reward Settings</h2>
                    <p>Define the rewards your participants can redeem with their {selectedProgram.asset_type === 'points_on_chain' ? 'tokens' : 'points'} on <strong>{selectedProgram.name}</strong>.</p>
                </div>
                <button className="primary-btn" onClick={openCreateForm} disabled={showForm}>+ Add reward</button>
            </div>

            {error && <p className="error-message">Error: {error}</p>}

            {showForm && (
                <form className="reward-form" onSubmit={handleSubmit}>
                    <h3>{editingId == null ? 'New reward' : 'Edit reward'}</h3>
                    <label>
                        Title
                        <input type="text" name="title" value={form.title} onChange={handleFormChange} maxLength={80} disabled={submitting} required />
                    </label>
                    <label>
                        Description
                        <textarea name="description" value={form.description} onChange={handleFormChange} rows={3} maxLength={500} disabled={submitting} />
                    </label>
                    <label>
                        Cost ({selectedProgram.asset_type === 'points_on_chain' ? `tokens${selectedProgram.asa_unit_name ? ` — ${selectedProgram.asa_unit_name}` : ''}` : 'points'})
                        <input type="number" name="cost" min="0" step="any" value={form.cost} onChange={handleFormChange} disabled={submitting} required />
                    </label>
                    <label className="image-label">
                        Image (recommended 800×600, max 2 MB)
                        <input type="file" name="image" accept="image/png,image/jpeg,image/gif,image/svg+xml" onChange={handleFormChange} disabled={submitting} />
                    </label>
                    <label className="checkbox-label">
                        <input type="checkbox" name="active" checked={form.active} onChange={handleFormChange} disabled={submitting} />
                        Active (visible to participants)
                    </label>
                    {formError && <p className="error-message">{formError}</p>}
                    <div className="form-actions">
                        <button type="submit" disabled={submitting} className="primary-btn">
                            {submitting ? 'Saving…' : (editingId == null ? 'Create reward' : 'Save changes')}
                        </button>
                        <button type="button" onClick={closeForm} disabled={submitting}>Cancel</button>
                    </div>
                </form>
            )}

            {isLoading && <p className="loading-message">Loading rewards…</p>}

            {!isLoading && !error && (
                rewards.length === 0 ? (
                    <p className="info-message">No rewards yet. Click <em>Add reward</em> to create your first one.</p>
                ) : (
                    <div className="reward-cards">
                        {rewards.map(r => {
                            const imgSrc = buildImgSrc(r.image_url);
                            return (
                                <div key={r.id} className={`reward-card ${r.active ? 'active' : 'inactive'}`}>
                                    <div className="reward-card-image">
                                        {imgSrc ? <img src={imgSrc} alt={r.title} /> : <div className="reward-image-placeholder">No image</div>}
                                    </div>
                                    <div className="reward-card-body">
                                        <h3>{r.title}</h3>
                                        {r.description && <p className="reward-desc">{r.description}</p>}
                                        <p className="reward-cost">{formatCost(r.cost, selectedProgram)}</p>
                                        <label className="checkbox-label">
                                            <input type="checkbox" checked={r.active} onChange={() => handleToggleActive(r)} />
                                            Active
                                        </label>
                                    </div>
                                    <div className="reward-card-actions">
                                        <button onClick={() => openEditForm(r)}>Edit</button>
                                        <button onClick={() => handleDelete(r)} className="danger-btn">Delete</button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            )}
        </div>
    );
};

export default RewardsPage;
